import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  MagnifyingGlass, 
  Plus, 
  Receipt,
  Sparkle,
  CaretDown,
  X,
  Check,
  Copy,
  FileText,
  CurrencyDollar,
  Stethoscope,
  FirstAidKit,
  Pill,
  ArrowClockwise,
  Warning,
  CheckCircle,
  Tag
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MedicalBillingPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('create'); // create, ai-assist, fee-schedule
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Code search states
  const [icd10Search, setIcd10Search] = useState('');
  const [cptSearch, setCptSearch] = useState('');
  const [hcpcsSearch, setHcpcsSearch] = useState('');
  const [icd10Codes, setIcd10Codes] = useState([]);
  const [cptCodes, setCptCodes] = useState([]);
  const [hcpcsCodes, setHcpcsCodes] = useState([]);
  
  // Selected codes for invoice
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  
  // AI Assist
  const [encounterNotes, setEncounterNotes] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Fee Schedule
  const [feeSchedule, setFeeSchedule] = useState([]);
  
  // Modifiers
  const [modifiers, setModifiers] = useState([]);
  const [modifierSearch, setModifierSearch] = useState('');
  
  // Invoice
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPatients();
    fetchCodes();
    if (activeTab === 'fee-schedule') {
      fetchFeeSchedule();
    }
    if (activeTab === 'modifiers') {
      fetchModifiers();
    }
  }, [activeTab]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/admin/users?role=patient');
      // Handle both array response and {users: [...]} response formats
      const patientList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setPatients(patientList);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  const fetchCodes = async () => {
    try {
      const [icd10Res, cptRes, hcpcsRes] = await Promise.all([
        api.get('/billing/codes/icd10'),
        api.get('/billing/codes/cpt'),
        api.get('/billing/codes/hcpcs')
      ]);
      setIcd10Codes(icd10Res.data.codes);
      setCptCodes(cptRes.data.codes);
      setHcpcsCodes(hcpcsRes.data.codes);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching codes:', error);
    }
  };

  const fetchFeeSchedule = async () => {
    try {
      const response = await api.get('/billing/fee-schedule');
      setFeeSchedule(response.data.fee_schedule);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching fee schedule:', error);
    }
  };

  const searchIcd10 = async (query) => {
    setIcd10Search(query);
    try {
      const response = await api.get(`/billing/codes/icd10?search=${query}`);
      setIcd10Codes(response.data.codes);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching ICD-10:', error);
    }
  };

  const searchCpt = async (query) => {
    setCptSearch(query);
    try {
      const response = await api.get(`/billing/codes/cpt?search=${query}`);
      setCptCodes(response.data.codes);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching CPT:', error);
    }
  };

  const searchHcpcs = async (query) => {
    setHcpcsSearch(query);
    try {
      const response = await api.get(`/billing/codes/hcpcs?search=${query}`);
      setHcpcsCodes(response.data.codes);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching HCPCS:', error);
    }
  };

  const fetchModifiers = async (query = '') => {
    try {
      const url = query 
        ? `/billing/codes/modifiers?search=${query}`
        : '/billing/codes/modifiers';
      const response = await api.get(url);
      setModifiers(response.data.modifiers || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching modifiers:', error);
    }
  };

  const searchModifiers = async (query) => {
    setModifierSearch(query);
    await fetchModifiers(query);
  };

  const filterModifiersByCategory = async (category) => {
    setModifierSearch('');
    try {
      const response = await api.get(`/billing/codes/modifiers?category=${category}`);
      setModifiers(response.data.modifiers || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error filtering modifiers:', error);
    }
  };

  const addDiagnosis = (code) => {
    if (!selectedDiagnoses.find(d => d.code === code.code)) {
      setSelectedDiagnoses([...selectedDiagnoses, code]);
    }
  };

  const removeDiagnosis = (code) => {
    setSelectedDiagnoses(selectedDiagnoses.filter(d => d.code !== code));
  };

  const addProcedure = (code, codeType = 'cpt') => {
    if (!selectedProcedures.find(p => p.code === code.code)) {
      setSelectedProcedures([...selectedProcedures, { ...code, code_type: codeType, quantity: 1 }]);
    }
  };

  const removeProcedure = (code) => {
    setSelectedProcedures(selectedProcedures.filter(p => p.code !== code));
  };

  const updateProcedureQuantity = (code, quantity) => {
    setSelectedProcedures(selectedProcedures.map(p => 
      p.code === code ? { ...p, quantity: Math.max(1, quantity) } : p
    ));
  };

  const calculateTotal = () => {
    return selectedProcedures.reduce((sum, p) => sum + (p.fee * p.quantity), 0);
  };

  const getAISuggestions = async () => {
    if (!encounterNotes.trim()) {
      toast.error('Please enter encounter notes');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await api.post('/billing/codes/ai-suggest', {
        encounter_notes: encounterNotes
      });
      setAiSuggestions(response.data);
      toast.success('AI suggestions generated!');
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    if (aiSuggestions) {
      // Add suggested diagnoses
      if (aiSuggestions.diagnosis_codes) {
        aiSuggestions.diagnosis_codes.forEach(d => {
          const matchingCode = icd10Codes.find(c => c.code === d.code);
          if (matchingCode && !selectedDiagnoses.find(sd => sd.code === d.code)) {
            setSelectedDiagnoses(prev => [...prev, matchingCode]);
          }
        });
      }
      // Add suggested procedures
      if (aiSuggestions.procedure_codes) {
        aiSuggestions.procedure_codes.forEach(p => {
          const matchingCode = cptCodes.find(c => c.code === p.code);
          if (matchingCode && !selectedProcedures.find(sp => sp.code === p.code)) {
            setSelectedProcedures(prev => [...prev, { ...matchingCode, code_type: 'cpt', quantity: 1 }]);
          }
        });
      }
      toast.success('AI suggestions applied!');
    }
  };

  const createInvoice = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }
    if (selectedDiagnoses.length === 0) {
      toast.error('Please add at least one diagnosis code');
      return;
    }
    if (selectedProcedures.length === 0) {
      toast.error('Please add at least one procedure code');
      return;
    }
    
    setCreating(true);
    try {
      const invoiceData = {
        patient_id: selectedPatient.id,
        diagnosis_codes: selectedDiagnoses.map(d => ({ code: d.code, description: d.description })),
        procedure_codes: selectedProcedures.map(p => ({
          code: p.code,
          code_type: p.code_type,
          description: p.description,
          quantity: p.quantity,
          fee: p.fee,
          diagnosis_codes: selectedDiagnoses.map(d => d.code)
        })),
        notes: invoiceNotes
      };
      
      await api.post('/billing/medical-invoice', invoiceData);
      toast.success('Invoice created successfully!');
      
      // Reset form
      setSelectedPatient(null);
      setSelectedDiagnoses([]);
      setSelectedProcedures([]);
      setInvoiceNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="medical-billing-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Medical Billing
        </h1>
        <p className="text-slate-500">Create invoices with ICD-10, CPT, and HCPCS codes</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'create', label: 'Create Invoice', icon: Receipt },
          { id: 'ai-assist', label: 'AI Code Assistant', icon: Sparkle },
          { id: 'modifiers', label: 'Modifier Codes', icon: Tag },
          { id: 'fee-schedule', label: 'Fee Schedule', icon: CurrencyDollar },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-teal-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create Invoice Tab */}
      {activeTab === 'create' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Patient & Codes Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Patient Selection */}
            <div className="card-clinical p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Patient</h2>
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search patients..."
                  className="input-clinical pl-10"
                />
              </div>
              {selectedPatient ? (
                <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                    <p className="text-sm text-slate-500">{selectedPatient.email}</p>
                  </div>
                  <button onClick={() => setSelectedPatient(null)} className="p-2 hover:bg-teal-100 rounded-lg">
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {filteredPatients.slice(0, 10).map((patient) => (
                    <button
                      key={patient.id}
                      onClick={() => setSelectedPatient(patient)}
                      className="w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100 text-left transition-colors"
                    >
                      <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                      <p className="text-sm text-slate-500">{patient.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ICD-10 Diagnosis Codes */}
            <div className="card-clinical p-6">
              <div className="flex items-center gap-2 mb-4">
                <FirstAidKit size={20} className="text-red-500" />
                <h2 className="text-lg font-semibold text-slate-900">ICD-10 Diagnosis Codes</h2>
              </div>
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={icd10Search}
                  onChange={(e) => searchIcd10(e.target.value)}
                  placeholder="Search ICD-10 codes..."
                  className="input-clinical pl-10"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {icd10Codes.slice(0, 20).map((code) => (
                  <div
                    key={code.code}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedDiagnoses.find(d => d.code === code.code)
                        ? 'bg-red-50 border-red-200'
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                    onClick={() => selectedDiagnoses.find(d => d.code === code.code) ? removeDiagnosis(code.code) : addDiagnosis(code)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono font-medium text-red-600">{code.code}</span>
                        <span className="text-xs ml-2 px-2 py-0.5 bg-slate-200 rounded-full">{code.category}</span>
                      </div>
                      {selectedDiagnoses.find(d => d.code === code.code) && (
                        <Check size={18} className="text-red-500" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{code.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CPT & HCPCS Procedure Codes */}
            <div className="card-clinical p-6">
              <div className="flex items-center gap-2 mb-4">
                <Stethoscope size={20} className="text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-900">CPT Procedure Codes</h2>
              </div>
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={cptSearch}
                  onChange={(e) => searchCpt(e.target.value)}
                  placeholder="Search CPT codes..."
                  className="input-clinical pl-10"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {cptCodes.slice(0, 20).map((code) => (
                  <div
                    key={code.code}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProcedures.find(p => p.code === code.code)
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                    onClick={() => selectedProcedures.find(p => p.code === code.code) ? removeProcedure(code.code) : addProcedure(code, 'cpt')}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono font-medium text-blue-600">{code.code}</span>
                        <span className="text-xs ml-2 px-2 py-0.5 bg-slate-200 rounded-full">{code.category}</span>
                        <span className="text-sm ml-2 font-medium text-green-600">${code.fee}</span>
                      </div>
                      {selectedProcedures.find(p => p.code === code.code) && (
                        <Check size={18} className="text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{code.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* HCPCS Codes */}
            <div className="card-clinical p-6">
              <div className="flex items-center gap-2 mb-4">
                <Pill size={20} className="text-purple-500" />
                <h2 className="text-lg font-semibold text-slate-900">HCPCS Codes</h2>
              </div>
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={hcpcsSearch}
                  onChange={(e) => searchHcpcs(e.target.value)}
                  placeholder="Search HCPCS codes..."
                  className="input-clinical pl-10"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {hcpcsCodes.slice(0, 20).map((code) => (
                  <div
                    key={code.code}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProcedures.find(p => p.code === code.code)
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                    onClick={() => selectedProcedures.find(p => p.code === code.code) ? removeProcedure(code.code) : addProcedure(code, 'hcpcs')}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono font-medium text-purple-600">{code.code}</span>
                        <span className="text-xs ml-2 px-2 py-0.5 bg-slate-200 rounded-full">{code.category}</span>
                        <span className="text-sm ml-2 font-medium text-green-600">${code.fee}</span>
                      </div>
                      {selectedProcedures.find(p => p.code === code.code) && (
                        <Check size={18} className="text-purple-500" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{code.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="space-y-6">
            <div className="card-clinical p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Invoice Summary</h2>
              
              {/* Selected Diagnoses */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Diagnosis Codes ({selectedDiagnoses.length})</h3>
                {selectedDiagnoses.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDiagnoses.map((d) => (
                      <div key={d.code} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div>
                          <span className="font-mono text-sm text-red-600">{d.code}</span>
                          <p className="text-xs text-slate-600 truncate max-w-[180px]">{d.description}</p>
                        </div>
                        <button onClick={() => removeDiagnosis(d.code)} className="p-1 hover:bg-red-100 rounded">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No diagnoses selected</p>
                )}
              </div>

              {/* Selected Procedures */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Procedure Codes ({selectedProcedures.length})</h3>
                {selectedProcedures.length > 0 ? (
                  <div className="space-y-2">
                    {selectedProcedures.map((p) => (
                      <div key={p.code} className="p-2 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm text-blue-600">{p.code}</span>
                          <button onClick={() => removeProcedure(p.code)} className="p-1 hover:bg-blue-100 rounded">
                            <X size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-slate-600 truncate">{p.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => updateProcedureQuantity(p.code, p.quantity - 1)}
                              className="w-6 h-6 bg-slate-200 rounded text-sm"
                            >-</button>
                            <span className="text-sm">{p.quantity}</span>
                            <button 
                              onClick={() => updateProcedureQuantity(p.code, p.quantity + 1)}
                              className="w-6 h-6 bg-slate-200 rounded text-sm"
                            >+</button>
                          </div>
                          <span className="font-medium text-green-600">${(p.fee * p.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No procedures selected</p>
                )}
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Invoice Notes</label>
                <textarea
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  placeholder="Add any notes..."
                  className="input-clinical h-20"
                />
              </div>

              {/* Total */}
              <div className="border-t border-slate-200 pt-4 mb-4">
                <div className="flex items-center justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-green-600">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Create Button */}
              <button
                onClick={createInvoice}
                disabled={creating || !selectedPatient || selectedDiagnoses.length === 0 || selectedProcedures.length === 0}
                className="w-full btn-teal flex items-center justify-center gap-2"
              >
                {creating ? <ArrowClockwise size={18} className="animate-spin" /> : <Receipt size={18} />}
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Code Assistant Tab */}
      {activeTab === 'ai-assist' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-clinical p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkle size={20} className="text-amber-500" />
              <h2 className="text-lg font-semibold text-slate-900">AI Code Suggestions</h2>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              Paste or type encounter notes and the AI will suggest appropriate ICD-10 and CPT codes.
            </p>
            
            <textarea
              value={encounterNotes}
              onChange={(e) => setEncounterNotes(e.target.value)}
              placeholder="Paste encounter notes here...&#10;&#10;Example: Patient presents with persistent cough for 5 days, low-grade fever of 100.2°F, and mild fatigue. Examination reveals clear lungs, no lymphadenopathy. Diagnosed with acute viral upper respiratory infection. Prescribed supportive care and rest."
              className="input-clinical h-64 mb-4"
            />
            
            <button
              onClick={getAISuggestions}
              disabled={aiLoading || !encounterNotes.trim()}
              className="w-full btn-teal flex items-center justify-center gap-2"
            >
              {aiLoading ? (
                <>
                  <ArrowClockwise size={18} className="animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkle size={18} />
                  Get AI Suggestions
                </>
              )}
            </button>
          </div>

          <div className="card-clinical p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Suggested Codes</h2>
            
            {aiSuggestions ? (
              <div className="space-y-4">
                {/* Diagnosis Suggestions */}
                {aiSuggestions.diagnosis_codes?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-2">ICD-10 Diagnoses</h3>
                    <div className="space-y-2">
                      {aiSuggestions.diagnosis_codes.map((d) => (
                        <div key={`diag-${d.code}`} className="p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-red-500" />
                            <span className="font-mono font-medium text-red-600">{d.code}</span>
                          </div>
                          <p className="text-sm text-slate-700 mt-1">{d.description}</p>
                          {d.rationale && (
                            <p className="text-xs text-slate-500 mt-1 italic">{d.rationale}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Procedure Suggestions */}
                {aiSuggestions.procedure_codes?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-2">CPT Procedures</h3>
                    <div className="space-y-2">
                      {aiSuggestions.procedure_codes.map((p) => (
                        <div key={`proc-${p.code}`} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-blue-500" />
                            <span className="font-mono font-medium text-blue-600">{p.code}</span>
                          </div>
                          <p className="text-sm text-slate-700 mt-1">{p.description}</p>
                          {p.rationale && (
                            <p className="text-xs text-slate-500 mt-1 italic">{p.rationale}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {aiSuggestions.notes && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">{aiSuggestions.notes}</p>
                  </div>
                )}

                <button
                  onClick={applyAISuggestions}
                  className="w-full btn-outline flex items-center justify-center gap-2"
                >
                  <Plus size={18} />
                  Apply Suggestions to Invoice
                </button>
              </div>
            ) : (
              <div className="p-8 bg-slate-50 rounded-xl text-center">
                <Sparkle size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-400">AI suggestions will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modifiers Tab */}
      {activeTab === 'modifiers' && (
        <div className="card-clinical p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">CPT Modifier Codes</h2>
              <p className="text-sm text-slate-500">Add modifiers to procedure codes for accurate billing</p>
            </div>
            <div className="relative w-64">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={modifierSearch}
                onChange={(e) => searchModifiers(e.target.value)}
                placeholder="Search modifiers..."
                className="input-clinical pl-10"
              />
            </div>
          </div>
          
          {/* Modifier Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {['E&M', 'Procedure', 'Anatomical', 'Service', 'Multiple', 'Anesthesia', 'Telehealth', 'Global'].map((cat) => (
              <button
                key={cat}
                onClick={() => filterModifiersByCategory(cat)}
                className="p-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors"
              >
                <span className="font-medium text-slate-700">{cat}</span>
                <span className="text-xs text-slate-500 ml-2">
                  (click to filter)
                </span>
              </button>
            ))}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-24">Modifier</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-32">Category</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 w-20">Copy</th>
                </tr>
              </thead>
              <tbody>
                {modifiers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-slate-500">
                      {modifierSearch ? 'No modifiers found' : 'Loading modifiers...'}
                    </td>
                  </tr>
                ) : (
                  modifiers.map((mod) => (
                    <tr key={`mod-${mod.code}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className="font-mono text-lg font-bold text-teal-600">{mod.code}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{mod.description}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          mod.category === 'E&M' ? 'bg-blue-100 text-blue-700' :
                          mod.category === 'Procedure' ? 'bg-purple-100 text-purple-700' :
                          mod.category === 'Anatomical' ? 'bg-green-100 text-green-700' :
                          mod.category === 'Service' ? 'bg-amber-100 text-amber-700' :
                          mod.category === 'Multiple' ? 'bg-red-100 text-red-700' :
                          mod.category === 'Anesthesia' ? 'bg-pink-100 text-pink-700' :
                          mod.category === 'Telehealth' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {mod.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(mod.code);
                            toast.success(`Modifier ${mod.code} copied!`);
                          }}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Copy modifier code"
                        >
                          <Copy size={16} className="text-slate-500" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Common Modifier Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-800 mb-2">Common Modifier Usage Tips</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li><strong>-25:</strong> Use when E&M is provided with a minor procedure on the same day</li>
              <li><strong>-59:</strong> Use to indicate a distinct procedural service when services might appear bundled</li>
              <li><strong>-LT/-RT:</strong> Required for procedures performed on one side of a bilateral body part</li>
              <li><strong>-26/-TC:</strong> Use to bill professional or technical component separately</li>
              <li><strong>-95:</strong> Required for synchronous telehealth services</li>
            </ul>
          </div>
        </div>
      )}

      {/* Fee Schedule Tab */}
      {activeTab === 'fee-schedule' && (
        <div className="card-clinical p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Fee Schedule</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Category</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Fee</th>
                </tr>
              </thead>
              <tbody>
                {feeSchedule.map((item) => (
                  <tr key={`fee-${item.code}-${item.code_type}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono text-sm">{item.code}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.code_type === 'CPT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {item.code_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{item.description}</td>
                    <td className="py-3 px-4 text-sm text-slate-500">{item.category}</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">${item.fee.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
